/**
 * Versioned URL hash codec: '#v=1&s=<seed>&preset=<id>&<key>=<val>...'
 *
 * - Human-readable key=value pairs, diff-from-baseline only.
 * - When a preset id is present, param pairs are diffs FROM THAT PRESET's
 *   baseline (DEFAULTS + preset diff), otherwise from DEFAULTS.
 * - Floats are quantized to the spec's step on encode.
 * - Decode is tolerant: unknown keys dropped, values clamped via
 *   sanitizeParam, unknown preset ids treated as absent. Returns null only
 *   for an empty hash.
 */
import { DEFAULTS, SPEC_BY_KEY, SPECS, sanitizeParam } from "../../engine/params";
import type { ParamKey, Params } from "../../engine/types";
import { presetById } from "./presets";

export interface ShareState {
  /** Empty string when the hash carried no seed — caller keeps its own. */
  seed: string;
  presetId: string | null;
  params: Partial<Params>;
}

const VERSION = "1";

function decimalsOf(step: number): number {
  return (step.toString().split(".")[1] ?? "").length;
}

/** Canonical string form of a param value; range values quantized to step. */
function formatValue(key: ParamKey, value: Params[ParamKey]): string {
  const spec = SPEC_BY_KEY.get(key);
  if (spec?.kind === "range" && typeof value === "number") {
    return value.toFixed(decimalsOf(spec.step));
  }
  return String(value);
}

function setParamValue<K extends ParamKey>(
  target: Partial<Params>,
  key: K,
  value: Params[K],
): void {
  target[key] = value;
}

export function encodeHash(state: {
  seed: string;
  presetId: string | null;
  params: Params;
}): string {
  const sp = new URLSearchParams();
  sp.set("v", VERSION);
  sp.set("s", state.seed);

  const preset = state.presetId ? presetById(state.presetId) : undefined;
  if (preset) sp.set("preset", preset.id);

  const baseline: Params = preset
    ? { ...DEFAULTS, ...preset.params }
    : { ...DEFAULTS };

  for (const spec of SPECS) {
    const key = spec.key;
    const current = formatValue(key, state.params[key]);
    if (current !== formatValue(key, baseline[key])) sp.set(key, current);
  }
  return `#${sp.toString()}`;
}

export function decodeHash(hash: string): ShareState | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;

  const sp = new URLSearchParams(raw);
  // `v` is assumed "1" when absent; unknown versions decode best-effort.
  const seed = sp.get("s") ?? "";
  const presetParam = sp.get("preset");
  const preset = presetParam ? presetById(presetParam) : undefined;

  const params: Partial<Params> = {};
  for (const [key, value] of sp) {
    if (key === "v" || key === "s" || key === "preset") continue;
    const clean = sanitizeParam(key as ParamKey, value);
    if (clean !== undefined) setParamValue(params, key as ParamKey, clean);
  }
  return { seed, presetId: preset?.id ?? null, params };
}
